import { MultiSelectJobApplication } from './types';
import { JobApplicationStatus, JobApplicationSubStatus, jobApplicationStatusLabels, jobApplicationSubStatusLabels } from './statuses';

/**
 * Returns the shared status if all selected job applications have the same status, otherwise 'mixed'.
 */
export const getMultiSelectJobApplicationStatus = (
    items: MultiSelectJobApplication[]
): string => {
    if (items.length === 0) return 'mixed';
    const first = items[0].status;
    return items.every(item => item.status === first) ? (first ?? 'mixed') : 'mixed';
};

const REJECTION_SUB_STATUSES = new Set<string>([
    JobApplicationSubStatus.NewRejected,
    JobApplicationSubStatus.InterviewRejected,
    JobApplicationSubStatus.TrialRejected,
    JobApplicationSubStatus.OfferRejected,
]);

// Status color mapping based on Figma design
export const getStatusColors = (status?: string, subStatus?: string) => {
    if (subStatus && REJECTION_SUB_STATUSES.has(subStatus)) {
        return {
            background: '#d34059',
            label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
            subLabel: `(${jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels]})`,
        };
    }
    switch (status) {
        case JobApplicationStatus.New:
            return {
                background: '#4239B0',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'envoyée'
                      })`
                    : '(envoyée)',
            };
        case JobApplicationStatus.Interview:
            return {
                background: '#2f80ed',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Programmé'
                      })`
                    : '(Programmé)',
            };
        case JobApplicationStatus.Trial:
            return {
                background: '#f2994a',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Programmé'
                      })`
                    : '(Programmé)',
            };
        case JobApplicationStatus.Offer:
            return {
                background: '#8dd690',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'envoyée'
                      })`
                    : '(envoyée)',
            };
        case JobApplicationStatus.ProbationPeriod:
            return {
                background: '#b08f38',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'sub-stage'
                      })`
                    : '(sub-stage)',
            };
        case JobApplicationStatus.Hired:
            return {
                background: '#118999',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          "Période d'essai"
                      })`
                    : "(Période d'essai)",
            };
        case JobApplicationStatus.Rejected:
            return {
                background: '#d34059',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Entretien'
                      })`
                    : '(Entretien)',
            };
        case 'BusinessCase':
            return {
                background: '#bb6bd9',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: '(A programmer)',
            };
        default:
            return {
                background: '#0c0d18',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: '(envoyée)',
            };
    }
};
